/**
 * THE LOADING STATE (P38, task 5 — audit S2-19, reference §12)
 *
 * =========================================================================
 * WHAT WAS MEASURED
 * =========================================================================
 * *"Measured after the SSO return: the entire document is the word `Loading…`*
 * *— no sidebar, no header, no skeleton."* And on every board, the same word
 * again once the shell was up, in place of the table.
 *
 * §12: *"the shell renders immediately, rows are grey bars, figures are dashes.
 * **Never zeros.**"*
 *
 * =========================================================================
 * WHY THE RULE SAYS "NEVER ZEROS" AND WHY IT MATTERED HERE
 * =========================================================================
 * A zero is a measurement. "0 open tickets" is a sentence about the system, and
 * while the request is still in flight it is a sentence nobody has any evidence
 * for — it is the same failure as the headline that read *"0 open"* with 41
 * tickets open (audit S1-03, fixed in `e338caf`), except produced by impatience
 * rather than by a filter.
 *
 * The ops strip used to avoid the zero by rendering **nothing at all** until the
 * counts arrived, which is honest and makes the header jump: the page grows a
 * 41 px band under the reader's cursor a second after they started reading. A
 * dash occupies the space and claims nothing.
 *
 * =========================================================================
 * WHY THE BARS DO NOT PULSE FOREVER, AND WHY THEY ARE NOT ANIMATED TEXT
 * =========================================================================
 * `animate-pulse` is Tailwind's own, it respects nothing by itself, so
 * `motion-reduce:animate-none` is on the bar: a reader who has asked their
 * system for less motion gets a static grey bar, which still says "something is
 * coming" by being the shape of a row.
 *
 * The count of bars is the SHAPE of what is coming, not a guess at the answer.
 * Ten rows on a board is the page's own furniture, the way a `<thead>` is; it is
 * not a claim that ten tickets exist, because a grey bar is not a number.
 */

const BAR = 'animate-pulse motion-reduce:animate-none rounded bg-hair-2';

/** One grey bar. `w` is a Tailwind width class, so a cell can be narrower than a row. */
export function SkeletonBar({ className = '' }: { className?: string }) {
    return <div className={`${BAR} h-3 ${className}`} aria-hidden="true" />;
}

/**
 * The shape of a table that has not arrived.
 *
 * It renders inside the SAME scrollport and the same `<table>` the real board
 * uses, so the header band, the column widths and the 44 px row do not move when
 * the data lands. A skeleton drawn in a different box is a second layout, and
 * the reader sees it shift.
 */
export function SkeletonRows({ rows = 10, columns = 8 }: { rows?: number; columns?: number }) {
    return (
        <>
            {Array.from({ length: rows }).map((_, r) => (
                <tr key={r} className="border-t border-hair-2">
                    {Array.from({ length: columns }).map((_, c) => (
                        <td key={c} className="h-row px-3">
                            {/* Varied widths, because a column of identical bars reads as a
                                rendered table of empty strings rather than as work in
                                progress. The pattern is deterministic — a random one would
                                re-shuffle on every re-render and flicker. */}
                            <SkeletonBar className={['w-3/4', 'w-1/2', 'w-5/6', 'w-2/3'][(r + c) % 4]} />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}

/**
 * A board's table shape while it loads: the real scrollport, a real header band
 * with the real column labels, skeleton rows below.
 *
 * The header is NOT skeletonised: the column names are known before the request
 * is made, they are part of the page and not part of the answer, and showing
 * them is what lets a reader see they are on the right board while it loads.
 */
export function SkeletonTable({ headers, rows = 10 }: { headers: string[]; rows?: number }) {
    return (
        <div className="mt-2 min-h-[240px] flex-1 overflow-auto rounded-card border border-hair bg-surface">
            <table className="w-full table-fixed border-collapse text-sm">
                <thead>
                    <tr>
                        {headers.map(h => (
                            <th
                                key={h}
                                className="h-9 border-b border-hair bg-head px-3 text-left text-xs font-bold uppercase tracking-wide text-muted"
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody aria-busy="true">
                    <SkeletonRows rows={rows} columns={headers.length} />
                </tbody>
            </table>
        </div>
    );
}

/**
 * The shape of a list of cards — `/authorizations`, and the panels on a ticket.
 */
export function SkeletonCards({ count = 3 }: { count?: number }) {
    return (
        <div className="mt-4 space-y-3" aria-busy="true">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="rounded-card border border-hair bg-surface p-4">
                    <SkeletonBar className="w-32" />
                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <SkeletonBar className="w-full" />
                        <SkeletonBar className="w-5/6" />
                        <SkeletonBar className="w-2/3" />
                    </div>
                    <SkeletonBar className="mt-3 w-1/2" />
                </div>
            ))}
        </div>
    );
}

/**
 * The one sentence that goes with any of the above, for a screen reader. The
 * bars themselves are `aria-hidden` — a dozen unlabelled divs announced one by
 * one is worse than silence — so the region says it once, politely.
 */
export function LoadingAnnouncement({ what }: { what: string }) {
    return <p role="status" aria-live="polite" className="sr-only">Loading {what}…</p>;
}
